import type {
  TemplateAudienceType,
  TemplateVariable,
  TemplateVariableScope,
} from "../api/queries/emailTemplateQueries";
import { LIST_ITEM_VARIABLE_KEYS } from "./emailTemplatePreview";

/** Variables available in subject/body for one invoice per email. */
const SINGLE_AUDIENCE_SCOPES: TemplateVariableScope[] = ["always", "list_item"];

/** Customer and sender placeholders (no invoice-level fields). */
const CUSTOMER_SENDER_SCOPES: TemplateVariableScope[] = ["always"];

/** Variables for each row inside {invoice_list} (multiple-invoice templates only). */
const LIST_ITEM_FORMAT_SCOPES: TemplateVariableScope[] = ["list_item"];

export const BODY_SCOPES_BY_AUDIENCE: Record<
  TemplateAudienceType,
  Set<TemplateVariableScope>
> = {
  SINGLE_INVOICE: new Set(SINGLE_AUDIENCE_SCOPES),
  MULTIPLE_INVOICES: new Set(CUSTOMER_SENDER_SCOPES),
};

export const SUBJECT_SCOPES_BY_AUDIENCE: Record<
  TemplateAudienceType,
  Set<TemplateVariableScope>
> = {
  SINGLE_INVOICE: new Set(SINGLE_AUDIENCE_SCOPES),
  MULTIPLE_INVOICES: new Set(CUSTOMER_SENDER_SCOPES),
};

export const LIST_ITEM_FORMAT_SCOPE_FILTER = new Set<TemplateVariableScope>(
  LIST_ITEM_FORMAT_SCOPES,
);

function isCustomerOrSenderVariable(variable: TemplateVariable): boolean {
  return (
    variable.scope === "always" &&
    variable.category !== "invoice"
  );
}

export function filterVariablesByScope(
  variables: TemplateVariable[],
  allowedScopes: Set<TemplateVariableScope>,
): TemplateVariable[] {
  return variables.filter((variable) => allowedScopes.has(variable.scope));
}

export function variablesForSubjectPicker(
  variables: TemplateVariable[],
  audienceType: TemplateAudienceType,
): TemplateVariable[] {
  if (audienceType === "SINGLE_INVOICE") {
    return filterVariablesByScope(variables, SUBJECT_SCOPES_BY_AUDIENCE.SINGLE_INVOICE);
  }
  return variables.filter(isCustomerOrSenderVariable);
}

export function variablesForBodyPicker(
  variables: TemplateVariable[],
  audienceType: TemplateAudienceType,
): TemplateVariable[] {
  if (audienceType === "SINGLE_INVOICE") {
    return filterVariablesByScope(variables, BODY_SCOPES_BY_AUDIENCE.SINGLE_INVOICE);
  }
  return variables.filter(
    (variable) =>
      variable.key === "invoice_list" || isCustomerOrSenderVariable(variable),
  );
}

export function variablesForListFormatPicker(
  variables: TemplateVariable[],
): TemplateVariable[] {
  return variables.filter(
    (variable) =>
      LIST_ITEM_FORMAT_SCOPE_FILTER.has(variable.scope) &&
      LIST_ITEM_VARIABLE_KEYS.has(variable.key),
  );
}

export function subjectVariablePickerMeta(
  audienceType: TemplateAudienceType,
): { label: string; description: string } {
  if (audienceType === "SINGLE_INVOICE") {
    return {
      label: "Subject variables (single invoice)",
      description:
        "Invoice-specific placeholders like {invoice_number} and {due_date} work in the subject.",
    };
  }
  return {
    label: "Subject variables",
    description:
      "Customer and sender placeholders only. Invoice details go in the body via {invoice_list}.",
  };
}

export function bodyVariablePickerMeta(
  audienceType: TemplateAudienceType,
): { label: string; description: string } {
  if (audienceType === "SINGLE_INVOICE") {
    return {
      label: "Body variables (single invoice)",
      description:
        "Click to insert placeholders for one invoice, customer, or sender fields.",
    };
  }
  return {
    label: "Body variables (multiple invoices)",
    description:
      "Insert {invoice_list} in the body. Set how each invoice row looks in the list format below.",
  };
}
